import { allUserRoles } from "constants/phase";
import { createSelector } from "reselect";
import { selectSelectedEntityRole } from "store/entity/selectors";

import { roleEntityAdapter, roleSlice } from "./slice";

const selectRoleState = (state) => state[roleSlice.name];

export const selectSelectedEntityRoleDetails = createSelector(
  selectRoleState,
  selectSelectedEntityRole,
  (userState, entityRole) => roleEntityAdapter.getSelectors().selectById(userState, entityRole) || null,
);

export const selectSelectedUserRolePrivileges = createSelector(
  selectSelectedEntityRoleDetails,
  (state) => state?.rolePrivileges || [],
);

export const selectSelectedUserPrivilegesByPage = createSelector(
  selectSelectedUserRolePrivileges,
  (state, props) => props.page,
  (privileges, page) => privileges.find((privilege) => privilege.name === page) || null,
);

export const selectSelectedUserPrivilegesByPageActions = createSelector(
  selectSelectedUserPrivilegesByPage,
  (privileges) => privileges?.actions || null,
);

export const selectSelectedUserIsProfileActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "profile")?.toggle ?? false,
);

export const selectSelectedUserIsUserManagementActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "user_management")?.toggle ?? false,
);

export const selectSelectedUserIsSettingsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "settings")?.toggle ?? false,
);

export const selectSelectedUserCreatePreviewActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "create_preview")?.toggle ?? false,
);

export const selectSelectedUserSaveAsClipActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "saveAsClip")?.toggle ?? false,
);

export const selectSelectedUserDownloadClipActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "downloadClip")?.toggle ?? false,
);

export const selectSelectedUserIsPermissionManagementActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) =>
    privilegesActions?.find((action) => action?.value === "permission_management")?.toggle ?? false,
);

export const selectSelectedUserAddNewVideoActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "add_new_video")?.toggle ?? false,
);

export const selectSelectedUserEditVideoActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "edit_video")?.toggle ?? false,
);

export const selectSelectedUserExportJsonActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "export_json")?.toggle ?? false,
);

export const selectSelectedUserExportMatchReportActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "export_match_report")?.toggle ?? false,
);

export const selectSelectedUserRetriggerVideoActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "re_trigger_video")?.toggle ?? false,
);

export const selectSelectedUserResendVideoActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "resendVideo")?.toggle ?? false,
);

export const selectSelectedUserMarkAsCompleteActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "markAsComplete")?.toggle ?? false,
);

export const selectSelectedUserViewDetailsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "view_details")?.toggle ?? false,
);

export const selectSelectedUserConvertVideoActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "convert_video")?.toggle ?? false,
);

export const selectSelectedUserInternalStatusActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "internal_status")?.toggle ?? false,
);

export const selectSelectedUserDeleteVideoActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "delete_video")?.toggle ?? false,
);

export const selectSelectedUserAddCategoryActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "add_category")?.toggle ?? false,
);

export const selectSelectedUserUpdateChannelActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "update_channel")?.toggle ?? false,
);

export const selectSelectedUserViewChannelActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "view_channel")?.toggle ?? false,
);

export const selectSelectedUserEditChannelDetailsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "edit_channelDetails")?.toggle ?? false,
);

export const selectSelectedUserEditChannelActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "edit_channel")?.toggle ?? false,
);

export const selectSelectedUserDeleteChannelActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "delete_channel")?.toggle ?? false,
);

export const selectSelectedUserAddCustomFieldsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "add_customFields")?.toggle ?? false,
);

export const selectSelectedUserEditCustomFieldsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "edit_customFields")?.toggle ?? false,
);

export const selectSelectedUserDeleteCustomFieldsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPageActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "delete_customFields")?.toggle ?? false,
);

export const selectSelectedUserAddServerActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "add_server")?.toggle ?? false,
);

export const selectSelectedUserUploadBumperActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "upload_bumper")?.toggle ?? false,
);

export const selectSelectedUserUploadGraphicsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "upload_graphics")?.toggle ?? false,
);

export const selectSelectedUserUploadOverlayActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "upload_overlay")?.toggle ?? false,
);

export const selectSelectedUserUploadSoundActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "upload_sound")?.toggle ?? false,
);

export const selectSelectedUserAddHashtagsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "add_hashtags")?.toggle ?? false,
);

export const selectSelectedUserViewBumperActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "view_bumper")?.toggle ?? false,
);

export const selectSelectedUserViewGraphicsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "view_graphics")?.toggle ?? false,
);

export const selectSelectedUserViewOverlayActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "view_overlay")?.toggle ?? false,
);

export const selectSelectedUserViewFadeInFadeOutActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) =>
    userPrivileges?.actions?.find((action) => action?.value === "fadeIn/Out" || action?.value === "fadeIn/fadeOut")
      ?.toggle ?? false,
);

export const selectSelectedUserViewClipInfoActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "clipInfo")?.toggle ?? false,
);

export const selectSelectedUserViewAudioActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "view_sound")?.toggle ?? false,
);

export const selectSelectedUserViewTransitionActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "apply_transition")?.toggle ?? false,
);

export const selectSelectedUserViewHashtagsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "view_hashtags")?.toggle ?? false,
);

export const selectSelectedUserViewFolderRatingsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "folder_rating")?.toggle ?? false,
);

export const selectSelectedUserPublishActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "publish")?.toggle ?? false,
);

export const selectSelectedUserOrganizationProfilePagePrivileges = createSelector(
  selectSelectedUserRolePrivileges,
  (privileges) => privileges.find((privilege) => privilege.name === "organization-profile") || null,
);

export const selectSelectedUserOrganizationProfilePagePrivilegesActions = createSelector(
  selectSelectedUserOrganizationProfilePagePrivileges,
  (privileges) => privileges?.actions || null,
);

export const selectSelectedUserDeleteSocialAccountsActionAllowed = createSelector(
  selectSelectedUserOrganizationProfilePagePrivilegesActions,
  (privilegesActions) =>
    privilegesActions?.find((action) => action?.value === "delete_socialAccounts")?.toggle ?? false,
);

export const selectSelectedUserCreateSocialAccountsActionAllowed = createSelector(
  selectSelectedUserOrganizationProfilePagePrivilegesActions,
  (privilegesActions) =>
    privilegesActions?.find((action) => action?.value === "create_socialAccounts")?.toggle ?? false,
);

export const selectSelectedUserEditSocialAccountsActionAllowed = createSelector(
  selectSelectedUserOrganizationProfilePagePrivilegesActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "edit_socialAccounts")?.toggle ?? false,
);

export const selectSelectedUserAddSocialMediaPlatformActionAllowed = createSelector(
  selectSelectedUserOrganizationProfilePagePrivilegesActions,
  (privilegesActions) =>
    privilegesActions?.find((action) => action?.value === "ayrshare_socialAccounts")?.toggle ?? false,
);

export const selectSelectedUserUpdateUserActionAllowed = createSelector(
  selectSelectedUserOrganizationProfilePagePrivilegesActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "update_user")?.toggle ?? false,
);

export const selectSelectedUserRemoveUserActionAllowed = createSelector(
  selectSelectedUserOrganizationProfilePagePrivilegesActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "remove_user")?.toggle ?? false,
);

export const selectSelectedUserAddUserActionAllowed = createSelector(
  selectSelectedUserOrganizationProfilePagePrivilegesActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "add_user")?.toggle ?? false,
);

export const selectSelectedUserAssignUserActionAllowed = createSelector(
  selectSelectedUserOrganizationProfilePagePrivilegesActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "assign_user")?.toggle ?? false,
);

export const selectSelectedUserOrganizationProfilePageAddReservedChannelActionAllowed = createSelector(
  selectSelectedUserOrganizationProfilePagePrivilegesActions,
  (privilegesActions) => privilegesActions?.find((action) => action?.value === "add_reserved_channel")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageEditServerActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "edit_storage")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageDeleteServerActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "delete_server")?.toggle ?? false,
);

export const selectSelectedUserEditReservedChannelActionAllowed = createSelector(
  selectSelectedUserOrganizationProfilePagePrivilegesActions,
  (privilegesActions) =>
    privilegesActions?.find((action) => action?.value === "edit_reserved_channel")?.toggle ?? false,
);

export const selectSelectedUserDeleteReservedChannelActionAllowed = createSelector(
  selectSelectedUserOrganizationProfilePagePrivilegesActions,
  (privilegesActions) =>
    privilegesActions?.find((action) => action?.value === "delete_reserved_channel")?.toggle ?? false,
);

export const selectSelectedUserAddFolderActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "add_folder")?.toggle ?? false,
);

export const selectSelectedUserEditSubClientsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "edit_sub_clients")?.toggle ?? false,
);

export const selectSelectedUserDeleteSubClientsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) =>
    userPrivileges?.actions?.find((action) => action?.value === "delete_sub_clients")?.toggle ?? false,
);

export const selectSelectedUserEditClientsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "edit_clients")?.toggle ?? false,
);

export const selectSelectedUserDeleteClientsActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "delete_clients")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageAddStorageActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "add_storage")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageSelectAdminUserActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "select_admin_user")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageEditStorageActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "edit_storage")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageEditTeamActionAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "edit_team")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageViewMediaContentAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) =>
    userPrivileges?.actions?.find((action) => action?.value === "view_media_content")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageViewSocialAccountsAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) =>
    userPrivileges?.actions?.find((action) => action?.value === "view_socialAccounts")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageViewSettingsAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "view_settings")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageEditCustomFieldsAllowed = createSelector(
  selectSelectedEntityRole,
  (role) => role === allUserRoles.superAdmin,
);

export const selectSelectedUserPrivilegesByPageEditTagsAllowed = createSelector(
  selectSelectedEntityRole,
  (role) => role === allUserRoles.superAdmin,
);

export const selectSelectedUserPrivilegesByPageEditTeamsAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "edit_team")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageDeleteTeamsAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "delete_team")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageAddTeamAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "add_team")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageDeletePlayerAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "delete_player")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageAddPlayerAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "add_player")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageDeleteCustomFieldsAllowed = createSelector(
  selectSelectedEntityRole,
  (role) => role === allUserRoles.superAdmin,
);

export const selectSelectedUserPrivilegesByPageDeleteTagsAllowed = createSelector(
  selectSelectedEntityRole,
  (role) => role === allUserRoles.superAdmin,
);

export const selectSelectedUserPrivilegesByPageAddCustomFieldsAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "add_customFields")?.toggle ?? false,
);
export const selectSelectedUserPrivilegesByPageAddEventFieldsAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "add_event")?.toggle ?? false,
);
export const selectSelectedUserPrivilegesByPageDeleteEventFieldsAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "delete_event")?.toggle ?? false,
);
export const selectSelectedUserPrivilegesByPageEditEventFieldsAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "edit_event")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageUpdateStorageAccessAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) =>
    userPrivileges?.actions?.find((action) => action?.value === "update_storage_access")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageDeleteStorageAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "delete_storage")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageViewStorageForAdminAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) =>
    userPrivileges?.actions?.find((action) => action?.value === "view_storage_for_admin")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageAddTournamentAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "add_tournament")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageAddNewsChannelAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "add_news_channel")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageAddFolder = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "add_folder")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageEditFolder = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "edit_folder")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageDeleteFolder = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "delete_folder")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageEditShowAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "edit_show")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageDeleteShowAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "delete_show")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageAddShowAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "add_show")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageAddDatafeedAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "add_datafeed")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageEditDatafeedAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "edit_datafeed")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageDeleteDatafeedAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "delete_datafeed")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageAdminWorkspaceAccess = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "admin_workspace")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageUserWorkspaceAccess = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "user_workspace")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageReviewerWorkspaceAccess = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) =>
    userPrivileges?.actions?.find((action) => action?.value === "reviewer_workspace")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageActionsWorkspaceAccess = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "actions_workspace")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageActionsWorkspaceActionsAccess = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) =>
    userPrivileges?.actions?.find((action) => action?.value === "actions_workspace_modal")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageAddWorkspaceUserAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) =>
    userPrivileges?.actions?.find((action) => action?.value === "add_user_workspace")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageDeleteClientWebhookAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) =>
    userPrivileges?.actions?.find((action) => action?.value === "delete_client_webhook")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageAddClientWebhookAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) =>
    userPrivileges?.actions?.find((action) => action?.value === "add_client_webhook")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageEditTournamentAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "edit_tournament")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageDeleteTournamentAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "delete_tournament")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageViewSecurityAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "view_security")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageAddWorkspaceAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "add_workspace")?.toggle ?? false,
);

export const selectSelectedUserPrivilegesByPageViewCategoryAllowed = createSelector(
  selectSelectedUserPrivilegesByPage,
  (userPrivileges) => userPrivileges?.actions?.find((action) => action?.value === "view_category")?.toggle ?? false,
);
